import posthog, { type CaptureResult } from 'posthog-js'

import { analyticsUrl, sanitizeAnalyticsProps } from './analytics-url'
import { publicConfigValue } from './public-config'

/**
 * Property keys whose value is a URL the SDK built for itself.
 *
 * Sanitizing at the one manual `$pageview` call is NOT enough: posthog-js
 * derives URLs from `window.location.href` on its own, for events this app
 * never issues. A visitor opening `/join?token=…` therefore shipped the
 * invitation bearer token to PostHog through several channels at once, and
 * each of the first two attempts at this fix closed only the channel it had
 * looked at. The list below is the result of reading what the SDK actually
 * sends rather than what it seemed likely to send:
 *
 *  - `$current_url` — on every event.
 *  - `$session_entry_url` / `$session_entry_referrer` — captured ONCE at
 *    session start and then attached to every subsequent event of that
 *    session. An invitee whose session begins on the invitation link carries
 *    the token on everything they do afterwards, including events fired long
 *    after they left the page.
 *  - `$referrer` / `$initial_referrer` / `$initial_current_url` — the previous
 *    and first pages, whole.
 *  - `$pathname` — already path-only, kept here so it cannot regress.
 *
 * NOT here, because they are not top-level string properties (see
 * `sanitizeElementHrefs`): autocapture's clicked-element href, which the SDK
 * nests inside `$elements` / `$elements_chain`.
 */
const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$session_entry_url',
  '$session_entry_referrer',
  '$referrer',
  '$initial_referrer',
  '$pathname',
]

/**
 * Autocapture records the clicked element's `href`, and on `/join` every link
 * points at `/login?next=<the whole invitation URL>` — a second copy of the
 * token. It is NOT a top-level property: the SDK puts it in `$elements[].
 * attr__href` and in the serialized `$elements_chain` string, so a
 * property-level rewrite never touches it.
 *
 * The chain is rewritten textually because that is the only shape it has by
 * the time a hook can see it. Both are handled, since which one is sent
 * depends on a per-project setting we do not control.
 */
function sanitizeElementHrefs(properties: Record<string, unknown>): void {
  const elements = properties.$elements
  if (Array.isArray(elements)) {
    for (const element of elements) {
      if (
        element &&
        typeof element === 'object' &&
        typeof (element as Record<string, unknown>).attr__href === 'string'
      ) {
        const el = element as Record<string, unknown>
        el.attr__href = analyticsUrl(el.attr__href as string)
      }
    }
  }

  const chain = properties.$elements_chain
  if (typeof chain === 'string' && chain.includes('href=')) {
    properties.$elements_chain = chain.replace(
      /href="([^"]*)"/g,
      (_match, href: string) => `href="${analyticsUrl(href)}"`,
    )
  }
}

let initPromise: Promise<void> | null = null

/**
 * Initialize PostHog once the public runtime config resolves (the key/host
 * come from `appConfig.getPublic`, with local-dev `VITE_*` overrides). Safe
 * to call repeatedly — one shared promise.
 */
export function initPostHog(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      const key = await publicConfigValue(
        'posthog.key',
        import.meta.env.VITE_POSTHOG_KEY,
      )
      if (!key) return
      const host = await publicConfigValue(
        'posthog.host',
        import.meta.env.VITE_POSTHOG_HOST,
      )
      posthog.init(key, {
        api_host: host || 'https://us.i.posthog.com',
        capture_pageview: false, // we track route changes manually via the router
        capture_pageleave: true,
        person_profiles: 'identified_only',
        // Capture nothing until the unified consent banner opts the user in.
        // ConsentBanner re-applies a stored 'granted' choice on load.
        opt_out_capturing_by_default: true,
        // `before_send` rather than `sanitize_properties`: the latter is
        // skipped entirely for `$snapshot`, and it is handed properties only,
        // so it cannot reach the element hrefs.
        before_send: sanitizeEvent,
        // Session replay is OFF while this app serves token-bearing URLs.
        //
        // Not a preference — a hook cannot make it safe. rrweb embeds the page
        // href inside `$snapshot_data`'s own Meta event, a nested structure no
        // property-level sanitizer can reach, and `$snapshot` skips PostHog's
        // property pipeline anyway. Nothing in this repo uses replay and
        // nobody had decided it was on; turning it on again means deciding
        // what to do about `/join?token=…` first.
        disable_session_recording: true,
      })
    })()
  }
  return initPromise
}

/**
 * Strip un-allowlisted query strings from everything URL-shaped on an event,
 * whatever the event is. Exported for the tests that pin the leak this closes.
 *
 * Runs as `before_send`, which sees the whole event — including `$snapshot`,
 * which `sanitize_properties` never receives.
 */
export function sanitizeEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event

  // ALL THREE bags. The `$initial_*` URLs are not top-level properties:
  // posthog-js builds them in `get_initial_props()` and assigns the result to
  // the event's own `$set_once`, which is written to the person profile
  // PERMANENTLY; `$set` is its mutable sibling, reached by `identify()` and
  // `setPersonProperties()`. Only the `$session_entry_*` family lands in
  // `properties`. Sanitizing one bag leaves the longest-lived copy of the URL
  // untouched — which is how an invitation token reached a person profile for
  // its full 7-day life.
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue
    for (const key of URL_PROPERTIES) {
      const value = bag[key]
      if (typeof value === 'string') bag[key] = analyticsUrl(value)
    }
    // The named pass above also catches a value the shape rule cannot — a
    // host-relative URL. This one catches a key the SDK's list cannot: the same
    // props object goes to both destinations (`analytics.ts`), so it runs the
    // SHARED walk the dataLayer leg runs, at every depth, rather than a second
    // hand-rolled traversal. Assigned in place so the SDK keeps the TOP-LEVEL
    // bag's identity; nested containers are replaced by sanitised copies.
    Object.assign(bag, sanitizeAnalyticsProps(bag))
  }

  if (event.properties) sanitizeElementHrefs(event.properties)
  return event
}

/**
 * posthog-js silently DROPS capture/identify/reset/opt calls made before
 * `init()` — and init now completes only after the runtime config fetch. All
 * PostHog usage must therefore go through this wrapper, which sequences after
 * init; callbacks on the same resolved promise run in call order, so event
 * ordering is preserved.
 */
export function withPostHog(fn: (ph: typeof posthog) => void): void {
  void initPostHog().then(() => fn(posthog))
}

export function optInPostHog(): void {
  withPostHog((ph) => ph.opt_in_capturing())
}

export function optOutPostHog(): void {
  withPostHog((ph) => ph.opt_out_capturing())
}

export { posthog }
